// Phase 24d · Event ticket purchase flow (paid events)
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Calendar, Clock, Gift, ShieldCheck, Users, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function eur(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function fmt(iso) {
    try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

export default function EventTicketPurchase() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [giftMode, setGiftMode] = useState(false);
    const [holderEmails, setHolderEmails] = useState([""]);
    const [inviteMessage, setInviteMessage] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/community/events/${id}`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    setEvent(d);
                    if ((d.pricing_type || "free") === "free") {
                        toast.info("Free event — redirecting to RSVP");
                        navigate(`/events/${d.slug || d.id}`);
                    }
                } else if (r.status === 503) setDisabled(true);
            } catch {}
            setLoading(false);
        })();
    }, [id, navigate]);

    useEffect(() => {
        // Adjust holder_emails array length to match quantity
        setHolderEmails((prev) => {
            const arr = [...prev];
            while (arr.length < quantity) arr.push("");
            return arr.slice(0, quantity);
        });
    }, [quantity]);

    useEffect(() => {
        if (!event) return;
        // JSON-LD offers
        const ld = {
            "@context": "https://schema.org",
            "@type": "Event",
            "name": event.title,
            "startDate": event.scheduled_start,
            "endDate": event.scheduled_end,
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
            "offers": {
                "@type": "Offer",
                "price": (Number(event.price_eur_cents || 0) / 100).toFixed(2),
                "priceCurrency": "USD",
                "availability": (event.capacity == null || (event.tickets_sold || 0) < event.capacity)
                    ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
                "url": `${window.location.origin}/event/${event.id}/ticket`,
            },
        };
        let s = document.getElementById("event-offer-ldjson");
        if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "event-offer-ldjson"; document.head.appendChild(s); }
        s.textContent = JSON.stringify(ld);
        return () => { const el = document.getElementById("event-offer-ldjson"); if (el) el.remove(); };
    }, [event]);

    const purchase = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const body = { event_id: event.id, quantity };
            if (giftMode) {
                body.holder_emails = holderEmails.filter(Boolean);
                if (inviteMessage) body.invite_message = inviteMessage;
            }
            const r = await fetch(`${API}/api/tickets/purchase`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (r.status === 401) {
                // Wire upsell context (public purchase attempt)
                try {
                    await fetch(`${API}/api/upsell/context/action-attempted`, {
                        method: "POST", credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            action: "paid_ticket_purchase",
                            target_id: event.id,
                            surface: "event_ticket_purchase",
                        }),
                    });
                } catch {}
                window.location.href = "/";
                return;
            }
            if (r.status === 503) { setDisabled(true); return; }
            const d = await r.json().catch(() => ({}));
            if (r.ok) {
                toast.success(`Purchased ${quantity} ticket${quantity > 1 ? "s" : ""}`);
                const first = (d.tickets || [])[0];
                navigate(`/tickets/success/${first?.ticket_id || event.id}`);
            } else {
                toast.error(d.detail?.error || d.detail || "Purchase failed");
            }
        } catch { toast.error("Network error"); }
        setBusy(false);
    };

    if (loading) return <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] flex items-center justify-center">Loading…</div>;
    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] flex items-center justify-center px-4">
                <div className="text-center max-w-md" data-testid="ticketing-disabled">
                    <div className="mx-auto w-14 h-14 rounded-full bg-[#111111] border border-[#FFFFFF]/30 flex items-center justify-center mb-3">
                        <Ticket className="text-[#FFFFFF]" />
                    </div>
                    <h1 className="text-xl font-bold mb-1">Ticketing coming soon</h1>
                    <p className="text-neutral-400 text-sm">Paid tickets are being rolled out. Please check back soon.</p>
                    <Link to="/events" className="text-sm text-[#CCCCCC] hover:underline mt-3 inline-block">← Back to events</Link>
                </div>
            </div>
        );
    }
    if (!event) return <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] flex items-center justify-center">Event not found</div>;

    const price = Number(event.price_eur_cents || 0);
    const totalCents = price * quantity;
    const soldOut = event.capacity != null && (event.tickets_sold || 0) + quantity > event.capacity;
    const seatsLeft = event.capacity != null ? Math.max(0, event.capacity - (event.tickets_sold || 0)) : null;
    const refundLabel = ({
        no_refunds: "No refunds",
        default_24h: "24h refund window",
        custom: `${event.refund_policy_custom_hours || 24}h refund window`,
    })[event.refund_policy || "default_24h"];

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to={`/events/${event.slug || event.id}`} className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Back
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold flex items-center gap-2">
                        <Ticket size={16} className="text-[#FFFFFF]" /> Buy tickets
                    </h1>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5" data-testid="ticket-event-summary">
                    <p className="text-[10px] uppercase tracking-widest text-neutral-300 mb-1">Paid event</p>
                    <h2 className="text-2xl font-bold mb-2">{event.title}</h2>
                    <div className="flex items-center gap-4 text-sm text-neutral-400 flex-wrap">
                        <span className="flex items-center gap-1"><Calendar size={14} /> {fmt(event.scheduled_start)}</span>
                        <span className="flex items-center gap-1"><Clock size={14} /> {event.duration_minutes}min</span>
                        <span className="flex items-center gap-1"><ShieldCheck size={14} /> {refundLabel}</span>
                        {seatsLeft != null && (
                            <span className="flex items-center gap-1"><Users size={14} /> {seatsLeft} seats left</span>
                        )}
                    </div>
                    <p className="mt-3 text-3xl font-bold">{eur(price)}<span className="text-sm text-neutral-500 font-normal"> / ticket</span></p>
                </div>

                <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5 space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-neutral-500">Quantity (max 10)</label>
                        <input
                            data-testid="ticket-quantity-input"
                            type="number" min={1} max={10} value={quantity}
                            onChange={(e) => setQuantity(Math.max(1, Math.min(10, Number(e.target.value))))}
                            className="mt-1 w-24 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-lg font-semibold"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            data-testid="ticket-gift-checkbox"
                            type="checkbox" checked={giftMode} onChange={(e) => setGiftMode(e.target.checked)}
                            className="accent-[#FFFFFF]"
                        />
                        <Gift size={14} /> Gift these tickets to specific emails
                    </label>
                    {giftMode && (
                        <div className="space-y-2">
                            {holderEmails.map((h, i) => (
                                <input
                                    key={i}
                                    data-testid={`ticket-holder-email-${i}`}
                                    placeholder={`Holder email ${i + 1}`}
                                    value={h}
                                    onChange={(e) => {
                                        const arr = [...holderEmails]; arr[i] = e.target.value; setHolderEmails(arr);
                                    }}
                                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm"
                                />
                            ))}
                            <textarea
                                data-testid="ticket-invite-message"
                                placeholder="Optional invite message"
                                value={inviteMessage}
                                onChange={(e) => setInviteMessage(e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm"
                                rows={2}
                            />
                        </div>
                    )}
                </div>

                <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-5 flex items-center justify-between">
                    <div>
                        <p className="text-xs text-neutral-500">Total</p>
                        <p className="text-3xl font-bold">{eur(totalCents)}</p>
                    </div>
                    <button
                        onClick={purchase} disabled={busy || soldOut}
                        data-testid="ticket-purchase-cta"
                        className="px-5 py-2.5 rounded-full bg-gradient-to-r from-neutral-500 to-neutral-500 text-white font-semibold text-sm disabled:opacity-40"
                    >
                        {busy ? "Processing…" : soldOut ? "Sold out" : `Purchase ${eur(totalCents)}`}
                    </button>
                </div>
            </main>
        </div>
    );
}
